import React from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Package, CheckCircle2, TrendingUp, Users, ArrowRight, Loader2,
  Clock, ImagePlus, X, MapPin, ScanLine, Printer
} from "lucide-react";
import useSeo from "../hooks/useSeo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BentoCard = ({ icon: Icon, title, body, span = "", dark = false }) => (
  <div
    className={`${span} rounded-xl p-7 border ${
      dark ? "bg-[#0A1128] border-white/10 text-white" : "bg-white border-[#E2E8F0] text-[#0A1128] hover:shadow-md hover:-translate-y-1 transition-all duration-200"
    }`}
  >
    <Icon size={22} className={dark ? "text-brand-red mb-4" : "text-brand-red mb-4"} />
    <div className={`font-display font-bold text-lg ${dark ? "text-white" : "text-[#0A1128]"}`}>{title}</div>
    <p className={`text-sm mt-2 leading-relaxed ${dark ? "text-white/70" : "text-[#475569]"}`}>{body}</p>
  </div>
);

const Step = ({ n, title, body }) => (
  <div className="flex gap-4">
    <div className="font-display font-black text-3xl text-brand-red leading-none w-12 shrink-0 tracking-tighter">{n}</div>
    <div>
      <div className="font-semibold text-[#0A1128]">{title}</div>
      <p className="text-sm text-[#475569] mt-1 leading-relaxed">{body}</p>
    </div>
  </div>
);

export default function PakkeshopPartnerPage() {
  useSeo({
    title: "Bliv EMA Pickup Point Partner · Tjen op til 7.500 kr/md · EMA Kurer ApS",
    description: "Bliv EMA Pickup Point partner. Tjen 2-6 kr pr pakke. Få flere kunder i butikken. Ansøg på 2 minutter — vi leverer pakkerne direkte til jeres butik på Sjælland.",
    path: "/bliv-pakkeshop",
  });

  const [form, setForm] = React.useState({
    shop_name: "", cvr: "", owner_name: "", owner_phone: "", owner_email: "",
    shop_address: "", shop_zip: "", shop_city: "",
    opening_hours: "Man-Fre 09-18, Lør 10-15",
    estimated_daily_pickups: 10, storage_space_sqm: 2,
    has_camera: false, has_signage_space: false,
    business_type: "kiosk", notes: "",
  });
  const [photos, setPhotos] = React.useState([]);
  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);

  const upd = (k) => (e) => setForm((s) => ({
    ...s,
    [k]: e.target.type === "checkbox" ? e.target.checked
       : e.target.type === "number" ? Number(e.target.value)
       : e.target.value,
  }));

  const onPhotos = (e) => {
    const files = Array.from(e.target.files || []);
    const accepted = files.filter((f) => f.type.startsWith("image/") && f.size < 5 * 1024 * 1024);
    if (accepted.length < files.length) toast.error("Nogle filer blev sprunget over (kun billeder < 5 MB)");
    setPhotos((s) => [...s, ...accepted].slice(0, 6));
  };

  const removePhoto = (i) => setPhotos((s) => s.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v));
      photos.forEach((p) => fd.append("photos", p));
      await axios.post(`${API}/pakkeshop/apply-with-photos`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setSubmitted(true);
      toast.success("Tak — vi vender tilbage inden for 2-3 hverdage");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Der opstod en fejl — prøv igen");
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F1F5F9] flex items-center justify-center px-5 py-20">
        <div className="max-w-md bg-white border border-[#E2E8F0] rounded-xl p-10 text-center shadow-sm" data-testid="pakkeshop-success">
          <CheckCircle2 size={48} className="mx-auto text-emerald-600" />
          <h1 className="font-display font-bold text-3xl text-[#0A1128] mt-5">Tak for din ansøgning</h1>
          <p className="text-[#475569] mt-3 leading-relaxed">
            Vi har sendt en bekræftelse til <strong className="text-[#0A1128]">{form.owner_email}</strong>.
            Vores team gennemgår din ansøgning og vender tilbage inden for 2-3 hverdage.
          </p>
          <Link to="/" className="btn-primary mt-6 inline-flex">← Tilbage til forsiden</Link>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="pakkeshop-partner-page" className="bg-white">
      {/* HERO — dark */}
      <section className="bg-[#0A1128] text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_30%,rgba(220,38,38,0.18),transparent_55%)] pointer-events-none" />
        <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20 lg:py-28 relative">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-red border border-brand-red/40 rounded-full px-3 py-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-brand-red animate-pulse" />
            EMA Pickup Point · Partner-program
          </div>
          <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter mt-6 leading-[1.02]">
            Bliv EMA Pickup Point.<br />
            <span className="text-brand-red">Tjen på pakker</span><br />
            der allerede kommer forbi.
          </h1>
          <p className="text-white/75 mt-6 text-lg max-w-3xl leading-relaxed">
            Driv en kiosk, café eller butik på Sjælland? Bliv del af det moderne danske pickup-netværk
            — vi leverer pakker til jer, jeres kunder henter dem. Du tjener på hver eneste pakke
            og får samtidig flere kunder i butikken.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <a href="#ansoegning" className="btn-primary text-base" data-testid="ps-cta-apply">
              Ansøg nu <ArrowRight size={18} />
            </a>
            <a href="#hvordan" className="border-2 border-white/20 hover:border-white/40 text-white font-bold rounded-lg px-6 py-3 transition-colors">
              Sådan virker det
            </a>
          </div>
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-6 text-white/80">
            <div><div className="font-display font-black text-3xl text-white">2-6 kr</div><div className="text-xs uppercase tracking-widest mt-1">pr. pakke</div></div>
            <div><div className="font-display font-black text-3xl text-white">Sjælland</div><div className="text-xs uppercase tracking-widest mt-1">dækningsområde</div></div>
            <div><div className="font-display font-black text-3xl text-white">2-3 dage</div><div className="text-xs uppercase tracking-widest mt-1">godkendelse</div></div>
            <div><div className="font-display font-black text-3xl text-white">30 dage</div><div className="text-xs uppercase tracking-widest mt-1">opsigelse</div></div>
          </div>
        </div>
      </section>

      {/* BENTO BENEFITS */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#475569]">Hvorfor blive partner?</div>
        <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0A1128] mt-3 max-w-2xl tracking-tight">
          Et professionelt netværk bygget til lokale butikker
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-10">
          <BentoCard icon={TrendingUp} title="Ekstra indtægt" body="2 kr per modtaget pakke + 3 kr per udlevering + 4-6 kr per retur. 20 pakker/dag = +3.000 kr/md." />
          <BentoCard icon={Users} title="Flere kunder i butikken" body="Folk kommer forbi for at hente pakker — og køber typisk noget mens de er der. Gratis fodtrafik." dark />
          <BentoCard icon={Package} title="Minimum 1-2 m² plads" body="Pakker fylder lidt. Vi henter retur dagligt. Du behøver kun et lille hjørne." />
          <BentoCard icon={Printer} title="Vi leverer udstyret" body="Thermal-printer, scanner og labels — EMA stiller udstyret til rådighed. Du bruger din egen telefon som scanner." dark />
          <BentoCard icon={ScanLine} title="Premium scanning-flow" body="QR-baseret modtag/udlever på sekunder. Ingen kø, ingen forveksling, ingen tabte pakker." />
          <BentoCard icon={Clock} title="Du sætter åbningstiderne" body="Vi leverer kun når du har åbent. 30 dages opsigelse fra begge sider — uden bindinger." />
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="hvordan" className="bg-[#F1F5F9] py-20">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#475569]">Sådan virker det</div>
          <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0A1128] mt-3 tracking-tight">På 5 enkle trin</h2>
          <div className="mt-10 space-y-8">
            <Step n="1" title="Ansøg på 2 minutter" body="Udfyld formularen nedenfor med dine butiks-detaljer + 1-3 billeder af butikken. Vi vender tilbage inden for 2-3 hverdage." />
            <Step n="2" title="Få din 4-cifrede PIN + udstyr" body="Ved godkendelse modtager du PIN-kode på email og udstyrspakke (printer, scanner, labels) sendt til butikken." />
            <Step n="3" title="Modtag pakker — scan ind" body="Når en EMA-chauffør leverer pakker, scanner du dem ind på portalen. Du får besked til kunden automatisk." />
            <Step n="4" title="Udlever til kunden + print labels" body="Kunden viser sin pickup-kode. Du printer labels direkte fra portalen. Du markerer pakken som udleveret." />
            <Step n="5" title="Ikke-afhentede pakker → retur" body="Efter 7 dage markeres ikke-afhentede pakker til retur. Du printer retur-label og EMA-chauffør henter dem." />
          </div>
        </div>
      </section>

      {/* COMMISSION */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-20">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#475569]">Hvad tjener jeg?</div>
        <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0A1128] mt-3 tracking-tight">Eksempler på månedlig indtjening</h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-7 shadow-sm">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#475569]">10 pakker/dag</div>
            <div className="font-display font-black text-4xl text-[#0A1128] mt-3 tracking-tight">~1.500 kr</div>
            <div className="text-xs uppercase tracking-widest text-[#475569] mt-1">om måneden</div>
            <div className="text-sm text-[#475569] mt-3">Stille kiosk eller lille café</div>
          </div>
          <div className="bg-[#0A1128] border border-brand-red rounded-xl p-7 text-white shadow-md">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-red">★ Mest populær</div>
            <div className="font-display font-black text-4xl text-white mt-3 tracking-tight">~3.750 kr</div>
            <div className="text-xs uppercase tracking-widest text-white/60 mt-1">om måneden</div>
            <div className="text-sm text-white/70 mt-3">25 pakker/dag — almindelig kiosk eller frisør</div>
          </div>
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-7 shadow-sm">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#475569]">50 pakker/dag</div>
            <div className="font-display font-black text-4xl text-[#0A1128] mt-3 tracking-tight">~7.500 kr</div>
            <div className="text-xs uppercase tracking-widest text-[#475569] mt-1">om måneden</div>
            <div className="text-sm text-[#475569] mt-3">Travl supermarked eller blomsterhandler</div>
          </div>
        </div>
        <p className="text-xs text-[#475569] mt-5">Baseret på 2 kr/modtaget + 3 kr/udleveret. Faktisk indtjening afhænger af lokal trafik og åbningstider. Returer giver 4-6 kr ekstra.</p>
      </section>

      {/* EQUIPMENT NOTE */}
      <section className="bg-[#F8FAFC] py-14 border-y border-[#E2E8F0]">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 lg:px-12 grid md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-red">Udstyrs-info</div>
            <h3 className="font-display font-bold text-2xl text-[#0A1128] mt-3 tracking-tight">EMA leverer udstyret</h3>
            <p className="text-[#475569] mt-3 leading-relaxed">
              Vi sender thermal-printer, QR/barcode-scanner og labels til alle godkendte partnere.
              Udstyret tilhører EMA Kurer ApS og må kun bruges til EMA Pickup Point — ikke til andre fragtfirmaer
              eller private formål. Ved ophør af samarbejde returneres alt udstyr.
            </p>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center">
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
              <Printer size={20} className="mx-auto text-[#0A1128]" />
              <div className="text-xs font-semibold text-[#0A1128] mt-2">Thermal printer</div>
            </div>
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
              <ScanLine size={20} className="mx-auto text-[#0A1128]" />
              <div className="text-xs font-semibold text-[#0A1128] mt-2">QR scanner</div>
            </div>
            <div className="bg-white border border-[#E2E8F0] rounded-xl p-4">
              <Package size={20} className="mx-auto text-[#0A1128]" />
              <div className="text-xs font-semibold text-[#0A1128] mt-2">Labels & ruller</div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-[#F1F5F9] py-16">
        <div className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12">
          <h2 className="font-display font-bold text-3xl text-[#0A1128] tracking-tight">Spørgsmål & svar</h2>
          <div className="mt-6 space-y-3">
            {[
              { q: "Hvor meget plads skal jeg bruge?", a: "Minimum 1-2 m² lukket plads (bagved disken eller i lager)." },
              { q: "Hvilket udstyr får jeg?", a: "Thermal-printer, QR-scanner og labels. EMA ejer udstyret og leverer det gratis. Udstyret skal returneres ved ophør." },
              { q: "Hvad sker hvis udstyret går i stykker?", a: "Almindeligt slid dækker vi. Ved skade pga. misbrug, tab eller manglende returnering opkræves erstatning svarende til udstyrets værdi." },
              { q: "Hvad sker hvis en kunde ikke henter sin pakke?", a: "Efter 7 dage markeres pakken til retur. Du printer retur-label fra portalen og EMA henter pakken næste hverdag. Du får 4-6 kr per retur." },
              { q: "Hvornår får jeg udbetalt?", a: "Månedligt — 1. i hver måned. Vi sender opgørelse + overfører direkte til den konto du opgiver." },
              { q: "Kan jeg afbryde samarbejdet?", a: "Ja. 30 dages opsigelse fra begge sider. Alt udstyr returneres ved ophør." },
              { q: "Er jeg ansvarlig hvis en pakke bortkommer?", a: "Nej — EMA Kurer's erhvervsforsikring dækker pakker, så længe du følger procedurerne." },
            ].map((f, i) => (
              <details key={i} className="bg-white border border-[#E2E8F0] rounded-xl p-5 group" data-testid={`pakkeshop-faq-${i}`}>
                <summary className="font-semibold text-[#0A1128] cursor-pointer flex justify-between items-center">
                  {f.q}
                  <span className="text-brand-red text-2xl leading-none group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-[#475569] leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* APPLICATION FORM */}
      <section id="ansoegning" className="max-w-3xl mx-auto px-5 sm:px-8 lg:px-12 py-20">
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-brand-red">Ansøgning</div>
        <h2 className="font-display font-bold text-3xl sm:text-4xl text-[#0A1128] mt-3 tracking-tight">
          Ansøg om at blive EMA Pickup Point
        </h2>
        <p className="text-[#475569] mt-3">Udfyld nedenfor — tager ca. 2 minutter.</p>

        <form onSubmit={submit} className="mt-8 space-y-5" data-testid="pakkeshop-apply-form">
          <div className="bg-white border border-[#E2E8F0] rounded-xl p-7 shadow-sm">
            <h3 className="font-display font-bold text-lg text-[#0A1128] mb-5">Om butikken</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="label-flat">Butikkens navn *</label>
                <input className="input-flat" value={form.shop_name} onChange={upd("shop_name")} required data-testid="ps-shop-name" />
              </div>
              <div>
                <label className="label-flat">CVR-nummer *</label>
                <input className="input-flat font-mono" value={form.cvr} onChange={upd("cvr")} required maxLength={8} data-testid="ps-cvr" />
              </div>
              <div>
                <label className="label-flat">Type forretning *</label>
                <select className="input-flat" value={form.business_type} onChange={upd("business_type")} data-testid="ps-type">
                  <option value="kiosk">Kiosk / nærbutik</option>
                  <option value="cafe">Café</option>
                  <option value="frisor">Frisør / skønhedssalon</option>
                  <option value="supermarked">Supermarked</option>
                  <option value="blomster">Blomsterhandler</option>
                  <option value="boghandel">Boghandel</option>
                  <option value="renseri">Renseri</option>
                  <option value="andet">Anden type</option>
                </select>
              </div>
              <div className="sm:col-span-2">
                <label className="label-flat">Adresse *</label>
                <input className="input-flat" value={form.shop_address} onChange={upd("shop_address")} placeholder="Gade + nr." required data-testid="ps-address" />
              </div>
              <div>
                <label className="label-flat">Postnummer *</label>
                <input className="input-flat" value={form.shop_zip} onChange={upd("shop_zip")} maxLength={4} required data-testid="ps-zip" />
              </div>
              <div>
                <label className="label-flat">By *</label>
                <input className="input-flat" value={form.shop_city} onChange={upd("shop_city")} required data-testid="ps-city" />
              </div>
              <div className="sm:col-span-2">
                <label className="label-flat">Åbningstider *</label>
                <input className="input-flat" value={form.opening_hours} onChange={upd("opening_hours")} required data-testid="ps-hours" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-xl p-7 shadow-sm">
            <h3 className="font-display font-bold text-lg text-[#0A1128] mb-2">Billeder af butikken</h3>
            <p className="text-xs text-[#475569] mb-4">Min. 1 billede (max 6). JPG/PNG/WEBP, max 5 MB pr. billede. Vis butikkens facade + indvendigt rum hvor pakker skal ligge.</p>
            <label className="border-2 border-dashed border-[#E2E8F0] hover:border-brand-red rounded-xl p-8 text-center cursor-pointer block transition-colors" data-testid="ps-photo-dropzone">
              <ImagePlus size={28} className="mx-auto text-[#475569]" />
              <div className="text-sm font-semibold text-[#0A1128] mt-2">Klik for at uploade billeder</div>
              <div className="text-xs text-[#475569] mt-1">eller drag & drop</div>
              <input type="file" multiple accept="image/jpeg,image/png,image/webp,image/heic,image/heif" className="hidden" onChange={onPhotos} data-testid="ps-photo-input" />
            </label>
            {photos.length > 0 && (
              <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-3" data-testid="ps-photo-preview">
                {photos.map((p, i) => (
                  <div key={i} className="relative aspect-square bg-[#F1F5F9] rounded-lg overflow-hidden border border-[#E2E8F0]">
                    <img src={URL.createObjectURL(p)} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removePhoto(i)} className="absolute top-1 right-1 bg-black/60 hover:bg-black/80 text-white rounded-full p-1" data-testid={`ps-photo-remove-${i}`}>
                      <X size={12} />
                    </button>
                    <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-black/70 to-transparent text-white text-[10px] px-2 py-1 truncate">
                      {p.name}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-xl p-7 shadow-sm">
            <h3 className="font-display font-bold text-lg text-[#0A1128] mb-5">Om dig (ejeren)</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="sm:col-span-2">
                <label className="label-flat">Dit fulde navn *</label>
                <input className="input-flat" value={form.owner_name} onChange={upd("owner_name")} required data-testid="ps-owner-name" />
              </div>
              <div>
                <label className="label-flat">Mobil *</label>
                <input className="input-flat" type="tel" value={form.owner_phone} onChange={upd("owner_phone")} placeholder="+45 12 34 56 78" required data-testid="ps-owner-phone" />
              </div>
              <div>
                <label className="label-flat">Email *</label>
                <input className="input-flat" type="email" value={form.owner_email} onChange={upd("owner_email")} required data-testid="ps-owner-email" />
              </div>
            </div>
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-xl p-7 shadow-sm">
            <h3 className="font-display font-bold text-lg text-[#0A1128] mb-5">Praktiske detaljer</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="label-flat">Forventede pakker pr. dag</label>
                <input className="input-flat" type="number" min={0} max={500} value={form.estimated_daily_pickups} onChange={upd("estimated_daily_pickups")} data-testid="ps-daily" />
              </div>
              <div>
                <label className="label-flat">Tilgængelig plads (m²)</label>
                <input className="input-flat" type="number" min={0} max={100} value={form.storage_space_sqm} onChange={upd("storage_space_sqm")} data-testid="ps-space" />
              </div>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-[#0A1128]">
                <input type="checkbox" checked={form.has_camera} onChange={upd("has_camera")} data-testid="ps-camera" />
                Vi har overvågningskamera
              </label>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-[#0A1128]">
                <input type="checkbox" checked={form.has_signage_space} onChange={upd("has_signage_space")} data-testid="ps-signage" />
                Vi har plads til EMA Pickup Point skilt
              </label>
              <div className="sm:col-span-2">
                <label className="label-flat">Bemærkninger</label>
                <textarea className="input-flat" rows={3} value={form.notes} onChange={upd("notes")} placeholder="Andet vi bør vide? Specielle krav, om I allerede har erfaring med andet fragtfirma osv." data-testid="ps-notes" />
              </div>
            </div>
          </div>

          <button type="submit" disabled={submitting || photos.length === 0} className="btn-primary w-full justify-center text-base py-4" data-testid="ps-submit">
            {submitting ? <><Loader2 className="animate-spin" size={16} /> Sender...</> : <>Send min ansøgning <ArrowRight size={16} /></>}
          </button>
          {photos.length === 0 && (
            <p className="text-xs text-brand-red text-center font-semibold">Tilføj mindst ét billede af butikken før du indsender</p>
          )}
          <p className="text-xs text-[#475569] text-center">
            Vi behandler din ansøgning fortroligt og vender tilbage inden for 2-3 hverdage.
            Ved at sende accepterer du EMA Pickup Point's <Link to="/handelsbetingelser" className="underline">betingelser</Link>.
          </p>
        </form>
      </section>
    </div>
  );
}
